import { Injectable } from '@angular/core';
import { DataType, IDataset, IDatasetControl, JsonObject } from '@se/cp.common';
import { DataBinding, Logger } from '@se/se.ui.html5';
import { find } from 'lodash-es';
import { CpDatasourceService } from './cp-datasource.service';

@Injectable({
    providedIn: 'root',
})
export class CpDatasetService {
    private dataset?: IDataset;

    constructor(
        private dataSources: CpDatasourceService,
        private logger: Logger,
    ) {}

    initialize(dataset: IDataset) {
        this.dataset = dataset;
    }

    getAdvancedBindingKeys(binding: DataBinding): string[] | undefined {
        return this.getNode(binding)?.advancedBindingKeys;
    }

    getAdvancedSourcesListByBinding(binding: DataBinding): string[] {
        const controlsIds: string[] = this.getAdvancedBindingKeys(binding) || [];
        const node = this.getNode(binding);
        return (
            node?.controls
                .filter((control) => controlsIds.includes(control.name))
                .map((control) => control.itemsSource || '')
                .filter(Boolean) || []
        );
    }

    getPlaceholder(binding: DataBinding, ...advValues: (string | null)[]): JsonObject | undefined {
        const node = this.getNode(binding);
        if (!node) {
            this.logger.debug(`No entity defined in dataset at ${binding.toStringSimple()}.`);
            return undefined;
        }

        const placeholder = this.buildPlaceholder(node);

        if (advValues.length > 0 && !node.advancedBindingKeys) {
            this.logger.error(`Trying to build regular entity "${binding.toStringSimple()}" as advanced.`);
            return undefined;
        }
        if (advValues.length === 0 && node.advancedBindingKeys) {
            this.logger.error(
                `Trying to build advanced entity "${binding.toStringSimple()}" but no advancedBinding value is specified.`,
            );
            return undefined;
        }

        if (advValues.length === 0 || !node.advancedBindingKeys) {
            return placeholder;
        }

        const numProps = Math.max(advValues.length, node.advancedBindingKeys.length);
        for (let i = 0; i < numProps; ++i) {
            const advValue = advValues[i];
            const advKey = node.advancedBindingKeys[i];

            if (!advKey) {
                this.logger.error(`Supplied more advanced values than allowed for binding ${binding}`);
                continue;
            }
            if (advValue === undefined) {
                this.logger.error(`Supplied less advanced values than required for binding ${binding}`);
                continue;
            }

            if (advValue === null) {
                placeholder[advKey] = null;
            } else {
                placeholder[advKey] = this.buildAdvancedBinding(node, advKey, advValue);
            }
        }

        return placeholder;
    }

    getNode(binding: DataBinding) {
        const searchBinding = binding.next.simple;
        let iter = this.dataset;
        for (const part of searchBinding) {
            iter =
                find(iter?.childrenArray, (child) => child.name === part.value) ||
                find(iter?.childrenObject, (child) => child.name === part.value);
        }

        if (!iter) {
            return null;
        }

        return iter;
    }

    getControl(binding: DataBinding): IDatasetControl | null {
        const parent = this.getNode(binding.entity);
        if (!parent) return null;
        const property = binding.propertyName;
        return parent.controls.find((control) => control.name === property) ?? null;
    }

    getControlDataType(binding: DataBinding): DataType | null {
        const control = this.getControl(binding);
        if (!control) return null;
        return control.dataType;
    }

    safeExit() {
        this.dataset = undefined;
    }

    private buildAdvancedBinding(node: IDataset, key: string, value: string) {
        const advancedBindingSource = node.controls.find((c) => c.name === key)?.itemsSource;
        if (!advancedBindingSource) {
            this.logger.error(`Advanced binding node ${node.name} has no itemsSource.`);
            return null;
        }

        const item = this.dataSources.getById(advancedBindingSource)?.find((p) => p.Id === value);
        if (!item) {
            this.logger.error(`Cannot find advanced binding value ${value} for ${node.name}.`);
            return null;
        }

        return item as any as JsonObject;
    }

    private buildPlaceholder(node: IDataset, placeholder: JsonObject = {}) {
        for (const control of node.controls ?? []) {
            placeholder[control.name] = control.defaultValue ?? null;
        }
        for (const child of node.childrenArray ?? []) {
            placeholder[child.name] = [];
        }
        for (const child of node.childrenObject ?? []) {
            placeholder[child.name] = null;
        }

        return placeholder;
    }
}
